import { ChangeDetectionStrategy, Component, Signal, computed, input } from '@angular/core';
import {
    CeButtonComponent,
    CeIconComponent,
    CeParameterListImports,
    CePillComponent,
    CePillState,
    CeTooltipDirective,
    Icons,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { formatTimeAndDate } from '@kempower-nx-workspace/global-toolkit';
import { severityToDescription, severityToIcon, type Severity } from '@kempower-nx-workspace/chargeye-station-alerts';
import { EnrichedFrontendNotification, GetNotificationEmailSentTimestampResponse } from '../../data-access/notification-data/notification-data.service';

interface ModuleErrorEntry {
    code: string;
    severity: string;
    description: string;
    severityIcon: keyof Icons | undefined;
    severityPillState: CePillState;
}

interface ModuleErrorSection {
    title: string;
    errors: ModuleErrorEntry[];
}

interface PmcPayloadEntry {
    module?: string;
    cabinet?: string;
    generation?: string;
    errors?: string[];
}

interface ErrorCodeDetail {
    code: string;
    severity?: string;
}

@Component({
    selector: 'ce-notification-pmc-fault-content',
    imports: [
        CeButtonComponent,
        CeIconComponent,
        CeParameterListImports,
        CePillComponent,
        CeTooltipDirective,
    ],
    styleUrl: './notification-pmc-fault-content.component.scss',
    templateUrl: './notification-pmc-fault-content.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationPmcFaultContentComponent {
    notification = input.required<EnrichedFrontendNotification>();
    emailTimestamp = input<GetNotificationEmailSentTimestampResponse | undefined>();
    detailsInClassicUrl = input.required<string>();
    tooltipText = input.required<string>();
    linkLabel = input.required<string>();

    hasMultipleEvents: Signal<boolean> = computed(() => this.notification().events.length > 1);

    formattedEvents = computed(() =>
        this.notification().events.map((event) => ({
            ...event,
            formattedTimestamp: event.timestamp ? formatTimeAndDate(event.timestamp) : '',
        })),
    );

    stationName: Signal<string> = computed(() => {
        const meta = this.notification().events[0]?.metadata;
        return meta?.['stationName'] ?? meta?.['stationId'] ?? '';
    });

    ownerOrganizationName: Signal<string> = computed(() => {
        return this.notification().events[0]?.metadata?.['ownerOrganizationName'] ?? '';
    });

    serialNumber: Signal<string> = computed(() => {
        return this.notification().events[0]?.metadata?.['serialNumber'] ?? '';
    });

    stationModel: Signal<string> = computed(() => {
        return this.notification().events[0]?.metadata?.['model'] ?? '';
    });

    firmwareVersion: Signal<string> = computed(() => {
        return this.notification().events[0]?.metadata?.['firmwareVersion'] ?? '';
    });

    locationName: Signal<string> = computed(() => {
        return this.notification().events[0]?.metadata?.['locationName'] ?? '';
    });

    severityMessage: Signal<string> = computed(() => {
        const severity = this.notification().events[0]?.metadata?.['highestSeverity'] ?? 'unknown';
        const payload = this.pmcPayload();
        const errorCount = payload.reduce((sum, entry) => sum + (entry.errors?.length ?? 0), 0);
        const isSingle = errorCount === 1;

        const messages: Record<string, string> = isSingle
            ? {
                  major: 'The error may require onsite investigation.',
                  warning: 'The error may require remote investigation.',
                  info: 'No action required, the error will clear on its own.',
              }
            : {
                  major: 'Some errors may require onsite investigation.',
                  warning: 'Some errors may require remote investigation.',
                  info: 'No action required, the errors will clear on their own.',
              };
        return messages[severity] ?? '';
    });

    pmcPayload: Signal<PmcPayloadEntry[]> = computed(() => {
        const payload = this.notification().events[0]?.metadata?.['payload'];
        return Array.isArray(payload) ? (payload as PmcPayloadEntry[]) : [];
    });

    pmcNotificationMessage: Signal<string> = computed(() => {
        const payload = this.pmcPayload();
        if (payload.length > 0) {
            const moduleCount = payload.length;
            const errorCount = payload.reduce((sum, entry) => sum + (entry.errors?.length ?? 0), 0);
            return `${moduleCount} power ${moduleCount === 1 ? 'module' : 'modules'} reported ${errorCount} ${errorCount === 1 ? 'error' : 'errors'}.`;
        }
        return this.notification().events[0]?.metadata?.['message'] ?? this.notification().body;
    });

    moduleErrorSections: Signal<ModuleErrorSection[]> = computed(() => {
        const payload = this.pmcPayload();
        const errorCodeDetails = this.notification().events[0]?.metadata?.['errorCodeDetails'];

        const errorDetailsMap = new Map<string, ErrorCodeDetail>();
        if (Array.isArray(errorCodeDetails)) {
            for (const detail of errorCodeDetails as ErrorCodeDetail[]) {
                if (detail?.code) {
                    errorDetailsMap.set(detail.code, detail);
                }
            }
        }

        return payload.map((entry) => {
            const moduleName = entry.module ?? '';
            const cabinetRaw = entry.cabinet ?? '';
            const cabinet = cabinetRaw.charAt(0).toLowerCase() + cabinetRaw.slice(1);
            const generation = entry.generation ?? '';
            const errorCodes = entry.errors ?? [];

            const title = `Power module ${moduleName} ${cabinet} (V${generation})`;

            const errors = errorCodes.map((code: string) => {
                const detail = errorDetailsMap.get(code);
                const severity = detail?.severity ?? '';
                return {
                    code,
                    severity,
                    description: this.#getSeverityDescription(severity),
                    severityIcon: this.#getSeverityIcon(severity),
                    severityPillState: this.#getSeverityPillState(severity),
                };
            });

            return { title, errors };
        });
    });

    // Description copy comes from the shared severity legend (single source of truth).
    #getSeverityDescription(severity: string | undefined): string {
        if (!severity) return '';
        return severityToDescription(severity as Severity);
    }

    #getSeverityPillState(severity: string | undefined): CePillState {
        if (!severity) return 'default';
        const states: Record<string, CePillState> = {
            unknown: 'default',
            info: 'default',
            warning: 'warning',
            major: 'error',
        };
        return states[severity] ?? 'default';
    }

    // Icon mapping comes from the shared severity model (single source of truth).
    // Unlike the previous local map, this also covers `critical` (→ error-3).
    #getSeverityIcon(severity: string | undefined): keyof Icons | undefined {
        if (!severity) return undefined;
        return severityToIcon(severity as Severity);
    }
}
